import { useEffect, useState } from "react";
import { auth, db, storage } from "./firebase";
import Toast from "./components/Toast";
import imageCompression from "browser-image-compression";
import PageLayout from "./components/PageLayout";
import PlanCard from "./components/PlanCard";
import { Link } from "react-router-dom";
import useAdmin from "./hooks/useAdmin";
import { migrateProfileEntitlements } from "./services/stripe";

import {
  doc,
  setDoc,
  getDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp
} from "firebase/firestore";

import {
  ref,
  uploadBytes,
  getDownloadURL
} from "firebase/storage";

function Account() {
  const { isAdmin } = useAdmin();
  const [username, setUsername] = useState("");
  const [originalUsername, setOriginalUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [location, setLocation] = useState("");
  const [bio, setBio] = useState("");
  const [instagram, setInstagram] = useState("");
  const [tiktok, setTiktok] = useState("");
  const [youtube, setYoutube] = useState("");
  const [website, setWebsite] = useState("");
  const [image, setImage] = useState(null);
  const [accountSaved, setAccountSaved] = useState(false);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);

  const [toast, setToast] = useState({
    title: "",
    message: "",
    type: "success"
  });

  const showToast = (title, message = "", type = "success") => {
    setToast({ title, message, type });

    setTimeout(() => {
      setToast({
        title: "",
        message: "",
        type: "success"
      });
    }, 3000);
  };

  const compressImage = async (file) => {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true
    };

    return await imageCompression(file, options);
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) { setProfileLoading(false); return; }

      const emailQuery = query(
        collection(db, "profiles"),
        where("email", "==", user.email)
      );

      const snapshot = await getDocs(emailQuery);

      snapshot.forEach((docItem) => {
        const data = docItem.data();

        if (data.userId === user.uid) {
          setProfile(data);
          setUsername(data.username || "");
          setOriginalUsername(data.username || "");
          setDisplayName(data.displayName || "");
          setLocation(data.location || "");
          setBio(data.bio || "");
          setInstagram(data.instagram || "");
          setTiktok(data.tiktok || "");
          setYoutube(data.youtube || "");
          setWebsite(data.website || "");
        }
      });
      setProfileLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const saveProfile = async () => {
    try {
      const user = auth.currentUser;

      if (!user) {
        showToast("You need to be logged in first.", "error");
        return;
      }

      const cleanedUsername = username.trim().toLowerCase();

      const previousUsername = originalUsername.trim().toLowerCase();
const usernameChanged =
  previousUsername && cleanedUsername !== previousUsername;

      if (!/^[a-z0-9_]{3,20}$/.test(cleanedUsername)) {
        showToast(
          "Invalid username",
          "Use 3–20 characters: letters, numbers, or underscores only.",
          "error"
        );
        return;
      }

      const reservedUsernames = [
        "admin",
        "administrator",
        "support",
        "staff",
        "mybuildid",
        "official",
        "api",
        "login",
        "signup",
        "garage",
        "discover",
        "spotlight"
      ];

      if (reservedUsernames.includes(cleanedUsername)) {
        showToast(
          "Username unavailable",
          "Please choose another username.",
          "error"
        );
        return;
      }

      const reservedUsernameDoc = await getDoc(
  doc(db, "reservedUsernames", cleanedUsername)
);

if (
  reservedUsernameDoc.exists() &&
  reservedUsernameDoc.data().ownerUid !== user.uid
) {
  showToast(
    "Username unavailable",
    "This username has already been used and is permanently reserved.",
    "error"
  );
  return;
}

      const existingUsernameDoc = await getDoc(
        doc(db, "profiles", cleanedUsername)
      );

      if (
        existingUsernameDoc.exists() &&
        existingUsernameDoc.data().userId !== user.uid
      ) {
        showToast("Username already taken.", "error");
        return;
      }

      const emailQuery = query(
        collection(db, "profiles"),
        where("email", "==", user.email)
      );

      const emailSnapshot = await getDocs(emailQuery);

      let emailTaken = false;
      let existingProfile = null;

      emailSnapshot.forEach((docItem) => {
        const data = docItem.data();

        if (data.userId === user.uid) {
          existingProfile = data;
        } else {
          emailTaken = true;
        }
      });

      if (usernameChanged) {
  const cooldownDays = 365;

  const lastChangedDate =
    existingProfile?.usernameLastChangedAt?.toDate?.();

  if (lastChangedDate) {
    const now = new Date();
    const daysSinceChange =
      (now - lastChangedDate) / (1000 * 60 * 60 * 24);

    if (daysSinceChange < cooldownDays) {
      const daysLeft = Math.ceil(cooldownDays - daysSinceChange);

      showToast(
        "Username locked",
        `You can change your username again in ${daysLeft} day${daysLeft === 1 ? "" : "s"}.`,
        "warning"
      );
      return;
    }
  }

  const confirmed = window.confirm(
    `Change username?\n\nYour public profile URL and QR code will be updated.\n\nYour previous username will be permanently reserved and cannot be used by another account.\n\nUsernames can be changed once every 365 days.`
  );

  if (!confirmed) return;
}

      if (emailTaken) {
        showToast(
          "Email already connected",
          "This email is already connected to another MyBuildID account. Please log in using the original sign-in method.",
          "error"
        );
        return;
      }

      let imageUrl = existingProfile?.imageUrl || "";

      if (image) {
        const imageRef = ref(storage, `profileImages/${user.uid}`);
        const compressedProfileImage = await compressImage(image);

        await uploadBytes(imageRef, compressedProfileImage);

        imageUrl = await getDownloadURL(imageRef);
      }

    const profilePayload = {
  username: cleanedUsername,
  usernameLowercase: cleanedUsername,
  onboardingComplete: true,
  displayName,
  location,
  bio,
  instagram,
  tiktok,
  youtube,
  website,
  imageUrl,
  email: user.email,
  userId: user.uid,
  garageCars: existingProfile?.garageCars || [],
  ...(existingProfile?.createdAt && {
    createdAt: existingProfile.createdAt
  }),
  ...(usernameChanged && {
    usernameLastChangedAt: serverTimestamp()
  })
};

await setDoc(
  doc(db, "profiles", cleanedUsername),
  profilePayload,
  { merge: true }
);

if (usernameChanged) {
  await migrateProfileEntitlements(previousUsername, cleanedUsername);
}

if (usernameChanged) {
  await setDoc(
    doc(db, "reservedUsernames", cleanedUsername),
    {
      username: cleanedUsername,
      ownerUid: user.uid,
      active: true,
      reservedAt: serverTimestamp()
    },
    { merge: true }
  );
}

if (usernameChanged) {
  await setDoc(
    doc(db, "reservedUsernames", previousUsername),
    {
      username: previousUsername,
      ownerUid: user.uid,
      active: false,
      currentUsername: cleanedUsername,
      reservedAt: serverTimestamp()
    },
    { merge: true }
  );

  await deleteDoc(doc(db, "profiles", previousUsername));
}

      setUsername(cleanedUsername);
      setProfile({ ...existingProfile, ...profilePayload });

      if (
        !existingProfile?.garageCars ||
        existingProfile.garageCars.length === 0
      ) {
        setAccountSaved(true);
      } else {
        setAccountSaved(false);
      }

      showToast("Account saved.");
    } catch (error) {
      alert(error.message);
      console.error(error);
    }
  };

  return (
    <>
      <PageLayout title="Account" maxWidth="460px">
        {isAdmin && <Link to="/admin/vehicle-artwork" style={{ display: "block", color: "#fff", padding: 16, marginBottom: 20, border: "1px solid #444", borderRadius: 12 }}>Admin → Vehicle Artwork</Link>}
        {!profileLoading && profile && <PlanCard profile={profile} />}
        <div style={cardStyle}>
          <input
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            style={inputStyle}
          />

          <div style={usernameWarningStyle}>
  <strong>Choose carefully.</strong> Your username becomes part of your
  public profile URL and QR code. Usernames can be changed once every <strong>365 days</strong>.
</div>

          <input
            placeholder="Display Name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            style={inputStyle}
          />

          <input
            placeholder="Location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            style={inputStyle}
          />

          <textarea
            placeholder="Bio"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            style={{ ...inputStyle, height: "100px" }}
            
          />

          <input
            placeholder="Instagram URL"
            value={instagram}
            onChange={(e) => setInstagram(e.target.value)}
            style={inputStyle}
          />

          <input
            placeholder="TikTok URL"
            value={tiktok}
            onChange={(e) => setTiktok(e.target.value)}
            style={inputStyle}
          />

          <input
            placeholder="YouTube URL"
            value={youtube}
            onChange={(e) => setYoutube(e.target.value)}
            style={inputStyle}
          />

          <input
            placeholder="Website URL"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            style={inputStyle}
          />

          <label style={labelStyle}>Profile Picture</label>

          <input
            type="file"
            accept="image/*"
            onChange={(e) => setImage(e.target.files[0])}
            style={fileInputStyle}
          />

          <button onClick={saveProfile} style={buttonStyle}>
            Save Account
          </button>

          {accountSaved && (
            <div style={nextStepBoxStyle}>
              <h2 style={{ marginTop: 0 }}>
                🎉 Welcome to MyBuildID!
              </h2>

              <p style={{ opacity: 0.8, lineHeight: "1.6" }}>
                Your account is ready.
                <br />
                <br />
                Now let's add your first vehicle and start documenting your build.
              </p>

              <button
                type="button"
                onClick={() => window.location.href = "/garage"}
                style={buttonStyle}
              >
                Add My First Build
              </button>
            </div>
          )}
        </div>
      </PageLayout>

      <Toast
        title={toast.title}
        message={toast.message}
        type={toast.type}
        onClose={() =>
          setToast({
            title: "",
            message: "",
            type: "success"
          })
        }
      />
    </>
  );
}

const cardStyle = {
  backgroundColor: "#181818",
  border: "1px solid #242424",
  padding: "24px",
  borderRadius: "20px",
  width: "100%",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  gap: "14px"
};

const inputStyle = {
  padding: "14px",
  borderRadius: "12px",
  border: "1px solid #2a2a2a",
  backgroundColor: "#1c1c1c",
  color: "white",
  boxSizing: "border-box",
  width: "100%",
  fontSize: "16px"
};

const labelStyle = {
  fontWeight: "bold",
  marginTop: "10px"
};

const fileInputStyle = {
  width: "100%",
  boxSizing: "border-box",
  color: "white",
  padding: "12px",
  backgroundColor: "#1c1c1c",
  border: "1px solid #2a2a2a",
  borderRadius: "12px",
  overflow: "hidden"
};

const buttonStyle = {
  padding: "14px",
  border: "none",
  borderRadius: "12px",
  cursor: "pointer",
  fontWeight: "bold",
  backgroundColor: "#e10600",
  color: "white"
};

const nextStepBoxStyle = {
  backgroundColor: "#1f1f1f",
  border: "1px solid #333",
  borderRadius: "14px",
  padding: "16px",
  marginTop: "8px"
};

const usernameWarningStyle = {
  fontSize: "16px",
  opacity: 0.75,
  lineHeight: "1.5",
  marginTop: "-6px",
  marginBottom: "4px"
};

export default Account;
